import Button from "@cs/components/src/Button";
import PageTitle from "@cs/components/src/PageTitle";
import { PageWrapperTwoColumn } from "@cs/components/src/PageWrapper";
import React, { useState } from "react";
import { Check, Trash2 } from "react-feather";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import Input from "@cs/components/src/Input";
import Checkbox from "@cs/components/src/Checkbox";
import ApiService from "../../services/ApiService";
import Section from "@cs/components/src/Section";
import Table from "@cs/components/src/ThemedTable";
import styled from "styled-components";
import Spinner from "@cs/components/src/Spinner";

const LabelText = styled.div`
  color: var(--gray100);
`;

const SecretInputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  column-gap: 10px;
  align-items: center;
  padding: 5px 10px 0;
`;

const ConfigureOrganizationEditScreen = () => {
  const { id: organizationId } = useParams<{ id: string }>();
  const [secretId, setSecretId] = useState("");
  const [secretValue, setSecretValue] = useState("");
  const [titleDraft, setTitleDraft] = useState<string | undefined>(undefined);
  const [titleJustSaved, setTitleJustSaved] = useState(false);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["organization", organizationId],
    queryFn: async (): Promise<OrganizationInfo> => {
      const response = await ApiService.fetch<{ data: OrganizationInfo }>(
        "GET",
        `/api/config/organization/${organizationId}`
      );
      return response.data;
    },
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const info = [
    { label: "System Domain ID", value: data?.systemDomainId },
    { label: "App Name", value: data?.appName },
    { label: "App ID", value: data?.appId },
    { label: "Base Url", value: data?.baseUrl },
  ];

  const addSecretMutation = useMutation({
    mutationFn: async () => {
      await ApiService.fetch(
        "PUT",
        `/api/config/organization/${organizationId}/secret/${secretId}`,
        {
          value: secretValue,
        }
      );
      setSecretId("");
      setSecretValue("");
      await refetch();
    },
  });

  const deleteSecretMutation = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await ApiService.fetch(
        "DELETE",
        `/api/config/organization/${organizationId}/secret/${id}`
      );
      await refetch();
    },
  });

  const setUseTeamsMutation = useMutation({
    mutationFn: async (useTeams: boolean) => {
      await ApiService.fetch(
        "PATCH",
        `/api/config/organization/${organizationId}/use_teams`,
        { useTeams }
      );
      await refetch();
    },
  });

  const setTitleMutation = useMutation({
    mutationFn: async (title: string) => {
      await ApiService.fetch(
        "PATCH",
        `/api/config/organization/${organizationId}/title`,
        { title }
      );
      setTitleDraft(undefined);
      setTitleJustSaved(true);
      queryClient.invalidateQueries({ queryKey: ["organizationList"] });
      await refetch();
    },
  });

  const deleteOrganization = useMutation({
    mutationFn: async () => {
      if (
        window.confirm(
          "Any installed plugins will no longer function unless this organization is re-added. Continue?"
        )
      ) {
        await ApiService.fetch(
          "DELETE",
          `/api/config/organization/${organizationId}`
        );
        queryClient.invalidateQueries({ queryKey: ["organizationList"] });
        navigate(`../organization`);
      }
    },
  });

  return (
    <div>
      <PageTitle
        title="Configure Organization"
        previousRoute="/config/organization"
        previousTitle="Organizations"
        error={
          isError
            ? "Problem loading organization"
            : addSecretMutation.isError
            ? "Error adding secret"
            : deleteSecretMutation.isError
            ? "Error deleting secret"
            : setTitleMutation.isError
            ? "Error saving title"
            : undefined
        }
        success={titleJustSaved ? "Title saved" : undefined}
      >
        <Button
          label="Delete Organization"
          icon={Trash2}
          onClick={() => deleteOrganization.mutate()}
          loading={deleteSecretMutation.isLoading}
          type="danger"
        />
      </PageTitle>

      {isLoading ? (
        <Spinner />
      ) : (
        !isError && (
          <PageWrapperTwoColumn>
            <Section title={data?.title}>
              <SecretInputRow style={{ gridTemplateColumns: "1fr auto" }}>
                <Input
                  label="Title"
                  value={titleDraft ?? data?.title ?? ""}
                  onChange={(value) => {
                    setTitleDraft(value);
                    setTitleJustSaved(false);
                  }}
                  onSubmit={() => {
                    const value = (titleDraft ?? "").trim();
                    if (value && value !== data?.title) {
                      setTitleMutation.mutate(value);
                    }
                  }}
                />
                <Button
                  label="Save"
                  icon={Check}
                  onClick={() =>
                    setTitleMutation.mutate((titleDraft ?? "").trim())
                  }
                  loading={setTitleMutation.isLoading}
                  type="success"
                  disabled={
                    titleDraft === undefined ||
                    !titleDraft.trim() ||
                    titleDraft.trim() === data?.title
                  }
                />
              </SecretInputRow>
              <Table
                columnGrid
                columns={[
                  { title: "Field", width: "1fr" },
                  { title: "Value", width: "2.5fr" },
                ]}
                rows={info.map(({ label, value }) => ({
                  id: label,
                  element: () => (
                    <>
                      <LabelText>{label}</LabelText>
                      <div>{value}</div>
                    </>
                  ),
                }))}
                autoHeight={{ twoColumn: true }}
              ></Table>
              <div style={{ padding: "5px 10px" }}>
                <Checkbox
                  label="Use Teams"
                  inlineLabel
                  value={data?.useTeams ?? true}
                  onChange={(value) => setUseTeamsMutation.mutate(value)}
                  disabled={setUseTeamsMutation.isLoading}
                  description="Turn off for orgs not yet migrated to iconik Teams."
                />
              </div>
            </Section>
            <Section title="Secrets">
              <SecretInputRow>
                <Input
                  label="Unique Identifier"
                  value={secretId}
                  onChange={setSecretId}
                />
                <Input
                  label="Value"
                  value={secretValue}
                  onChange={setSecretValue}
                />

                <Button
                  label="Add Secret"
                  icon={Check}
                  onClick={() => addSecretMutation.mutate()}
                  loading={addSecretMutation.isLoading}
                  type="success"
                  disabled={!secretId || !secretValue}
                />
              </SecretInputRow>
              <Table
                columnGrid
                columns={[
                  { title: "Identifier", width: "1fr" },
                  { title: "", width: "auto" },
                ]}
                rows={data?.secrets.map(({ id }) => ({
                  id: id,
                  element: () => (
                    <>
                      <div>{id}</div>
                      <Button
                        label="Delete"
                        icon={Trash2}
                        onClick={() => deleteSecretMutation.mutate({ id })}
                        loading={deleteSecretMutation.isLoading}
                        type="danger"
                      />
                    </>
                  ),
                }))}
                autoHeight={{ twoColumn: true }}
              ></Table>
            </Section>
          </PageWrapperTwoColumn>
        )
      )}
    </div>
  );
};

export default ConfigureOrganizationEditScreen;
